import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@independent-software/typeui/controls/Button';
import { Dialog } from '@independent-software/typeui/controls/Dialog';
import { Loader } from '@independent-software/typeui/controls/Loader';
import { Icon } from '@independent-software/typeui/controls/Icon';
import { ToastService } from '@independent-software/typeui/controls/Toast';
import { Message } from '@independent-software/typeui/controls/Message';

import { AuthContext } from '../../AuthContext';
import { Container, Content, BottomBar, Section } from '../../modules';
import { SampleForm } from './SampleForm';
import { SampleFactory, Sample } from '../../resource/'; 

type TStep = 'loading' | 'loadError' | 'ready' | 'saving' | 'saveError' | 'error';

const EditSample = () => {
  const auth = React.useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const sample = React.useRef(null);

  const [step, setStep] = React.useState('loading' as TStep);
  const [valid, setValid] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [formData, setFormData] = React.useState(sample.current);
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => loadItem(), []);

  const loadItem = () => {
    SampleFactory.get(auth, parseInt(id))
    .then(data => {
      sample.current = data;
      setFormData(data);
      setStep('ready');
    })
    .catch(error => {
      setStep('loadError');
      setError(error);
    });
  }

  const handleChange = (data: Sample, forceupdate: boolean) => {
    sample.current = Object.assign(sample.current, data);
    if(forceupdate) setFormData({...data});
  }

  const handleValidate = (_valid: boolean) => setValid(_valid);

  const handleSubmit = () => {
    setFormData(sample.current);
    setDirty(true);
    if(!valid) return;
    setStep('saving');

    sample.current.$update(auth)
    .then((res:any) => {
      ToastService.toast("Sample updated.");
      navigate(-1);
    })
    .catch((error:any) => {
      setStep('saveError');
      setError(error);
    });
  }

  const handleCancelLoad = () => setStep('error');

  const handleRetryLoad = () => {
    setStep('loading');
    setError(null);
    loadItem();
  }
  
  const handleCancelSave = () => setStep('ready');

  return (
    <Container>
      {(step == 'loading' || step == 'saving') && <Loader/>}
      {step != 'loading' && step != 'loadError' && step != 'error' && 
      <React.Fragment>
        <Content>
          <SampleForm dirty={dirty} data={formData} onChange={handleChange} onValidate={handleValidate}/>
        </Content>
        <BottomBar>
          <Button primary disabled={!valid} onClick={handleSubmit}><Icon name="save"/> Save</Button>
          <Button secondary onClick={() => navigate(-1)}>Cancel</Button>
        </BottomBar>
      </React.Fragment>}
      {step == 'error' && <Content>
        <Section padded>
          <Message type="error">The requested data could not be retrieved from the server.</Message>
        </Section>
      </Content>}
      <Dialog.Xhr open={step == 'loadError'} error={error} onClose={handleCancelLoad} onRetry={handleRetryLoad}/>
      <Dialog.Xhr open={step == 'saveError'} error={error} onClose={handleCancelSave} onRetry={handleSubmit}/>
    </Container>
  );
}

export { EditSample }

